import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Loader2, Save } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  PromptSettingsSchema,
  promptSettingsSchema,
  useUpdatePromptSettings,
} from "@/lib/api/admin.ts";
import TextareaAutosize from "react-textarea-autosize";

interface PromptSettingsProps {
  defaultValues: PromptSettingsSchema;
}

export function PromptSettings({ defaultValues }: PromptSettingsProps) {
  const { mutateAsync: updatePromptSettings, isPending } =
    useUpdatePromptSettings();

  const form = useForm<PromptSettingsSchema>({
    resolver: zodResolver(promptSettingsSchema),
    defaultValues,
  });

  const handleSubmit = async (data: PromptSettingsSchema) => {
    try {
      await updatePromptSettings(data);
    } catch (error) {
      // Handle error, e.g., show an error message
      console.error("Failed to update settings:", error);
    }
  };

  return (
    <div className="rounded-xl border border-gray-100 p-4">
      <h2 className="text-lg font-bold">AI Settings</h2>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="mt-6 space-y-6"
        >
          <FormField
            control={form.control}
            name="chatSystemPrompt"
            render={({ field }) => (
              <FormItem>
                <FormLabel>AI Chat Prompt</FormLabel>
                <FormControl>
                  <TextareaAutosize
                    className="w-full rounded-md border border-gray-200 p-2"
                    placeholder="Enter AI Prompt"
                    minRows={5}
                    {...field}
                  />
                </FormControl>
                <FormMessage />

                <span className="text-sm text-gray-500">
                  Available variables: <code>{"{mentee_name}"}</code>,{" "}
                  <code>{"{mentor_name}"}</code>
                </span>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="suggestedRepliesPrompt"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Suggested Replies Prompt</FormLabel>
                <FormControl>
                  <TextareaAutosize
                    className="w-full rounded-md border border-gray-200 p-2"
                    placeholder="Enter Suggested Replies Prompt"
                    minRows={5}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="mentorSubheadingGeneratorPrompt"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mentor Subheading Generator Prompt</FormLabel>
                <FormControl>
                  <TextareaAutosize
                    className="w-full rounded-md border border-gray-200 p-2"
                    placeholder="Enter Mentor Subheading Generator Prompt"
                    minRows={5}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="onboardingThirdMessage"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Teen Onboarding Generated Third Message</FormLabel>
                <FormControl>
                  <TextareaAutosize
                    className="w-full rounded-md border border-gray-200 p-2"
                    placeholder="Enter Teen Onboarding Generated Third Message"
                    minRows={5}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="suggestedOpenerPrompt"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Suggested Opener AI Prompt</FormLabel>
                <FormControl>
                  <TextareaAutosize
                    className="w-full rounded-md border border-gray-200 p-2"
                    placeholder="Enter Suggested Opener AI Prompt"
                    minRows={5}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
                <span className="text-sm text-gray-500">
                  Available variables: <code>{"{teenName}"}</code>,{" "}
                  <code>{"{originalResponse}"}</code>,{" "}
                  <code>{"{enhancedResponse}"}</code>,{" "}
                  <code>{"{teenMemories}"}</code>
                </span>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="messageCount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Message Count</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="Enter Message Count"
                    {...field}
                    onChange={(e) =>
                      field.onChange(parseInt(e.target.value, 10))
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end">
            <Button type="submit" disabled={isPending}>
              {isPending ? (
                <Loader2 className="mr-1 h-5 w-5 animate-spin" />
              ) : (
                <Save className="mr-1 h-5 w-5" />
              )}
              Save
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
